import { useState } from "react";
import { Link } from "react-router-dom";
import { BackButton } from "@/components/ui/back-button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { ArrowRight, Briefcase, Building, FileText, Users } from "lucide-react";
import { useLanguage, Language } from "@/hooks/use-language";
import { getTranslation } from "@/lib/translations";

const getComplexServices = (language: Language) => {
  const services = {
    pl: [
      {
        title: "Rejestracja spółek",
        description: "Pełna obsługa procesu rejestracji spółek różnych form prawnych. Przygotowanie wszystkich niezbędnych dokumentów, reprezentacja w urzędach.",
        icon: <Building className="h-8 w-8 text-primary" />,
        services: [
          "Rejestracja sp. z o.o.",
          "Rejestracja jednoosobowej działalności gospodarczej",
          "Rejestracja spółek",
          "Przygotowanie dokumentów założycielskich",
          "Reprezentacja w KRS"
        ]
      },
      {
        title: "Usługi prawne",
        description: "Kompleksowa obsługa prawna dla firm i osób prywatnych. Konsultacje, przygotowanie dokumentów, reprezentacja interesów.",
        icon: <FileText className="h-8 w-8 text-primary" />,
        services: [
          "Konsultacje prawne",
          "Przygotowanie i analiza umów",
          "Prawo korporacyjne",
          "Prawo pracy",
          "Reprezentacja w sądach"
        ]
      },
      {
        title: "Usługi księgowe",
        description: "Pełen zakres usług księgowych dla firm i przedsiębiorców. Prowadzenie księgowości, sprawozdawczość podatkowa, optymalizacja podatkowa.",
        icon: <Briefcase className="h-8 w-8 text-primary" />,
        services: [
          "Prowadzenie księgowości",
          "Przygotowanie i składanie deklaracji podatkowych",
          "Naliczanie wynagrodzeń",
          "Optymalizacja podatkowa",
          "Przygotowanie raportów rocznych"
        ]
      },
      {
        title: "Legalizacja pobytu",
        description: "Pomoc w przygotowaniu dokumentów dla legalnego pobytu cudzoziemców w Polsce. Uzyskiwanie pozwoleń na pobyt, pracę, studia.",
        icon: <Users className="h-8 w-8 text-primary" />,
        services: [
          "Uzyskanie karty pobytu",
          "Pozwolenia na pracę",
          "Rejestracja PESEL",
          "Wizy dla członków rodziny",
          "Konsultacje w sprawach legalizacji"
        ]
      }
    ],
    en: [
      {
        title: "Company Registration",
        description: "Full support for the company registration process for various forms of ownership. Preparation of all necessary documents, representation in government agencies.",
        icon: <Building className="h-8 w-8 text-primary" />,
        services: [
          "LLC Registration (Sp. z o.o.)",
          "Individual Entrepreneur Registration (JDG)",
          "Partnership Registration",
          "Preparation of Founding Documents",
          "Representation in KRS"
        ]
      },
      {
        title: "Legal Services",
        description: "Comprehensive legal services for businesses and individuals. Consultations, document preparation, representation of interests.",
        icon: <FileText className="h-8 w-8 text-primary" />,
        services: [
          "Legal Consultations",
          "Preparation and Analysis of Contracts",
          "Corporate Law",
          "Labor Law",
          "Representation in Courts"
        ]
      },
      {
        title: "Accounting Services",
        description: "Full range of accounting services for companies and private entrepreneurs. Bookkeeping, tax reporting, tax optimization.",
        icon: <Briefcase className="h-8 w-8 text-primary" />,
        services: [
          "Bookkeeping",
          "Preparation and Submission of Tax Reports",
          "Payroll Calculation",
          "Tax Optimization",
          "Preparation of Annual Reports"
        ]
      },
      {
        title: "Residence Legalization",
        description: "Assistance in preparing documents for the legal stay of foreigners in Poland. Obtaining residence permits, work permits, study permits.",
        icon: <Users className="h-8 w-8 text-primary" />,
        services: [
          "Obtaining Residence Card",
          "Work Permits",
          "PESEL Registration",
          "Visas for Family Members",
          "Consultations on Legalization Issues"
        ]
      }
    ],
    uk: [
      {
        title: "Реєстрація компаній",
        description: "Повний супровід процесу реєстрації компаній різних форм власності. Підготовка всіх необхідних документів, представництво в державних органах.",
        icon: <Building className="h-8 w-8 text-primary" />,
        services: [
          "Реєстрація ТОВ (Sp. z o.o.)",
          "Реєстрація індивідуального підприємця (JDG)",
          "Реєстрація товариств",
          "Підготовка установчих документів",
          "Представництво в KRS"
        ]
      },
      {
        title: "Юридичні послуги",
        description: "Комплексне юридичне обслуговування бізнесу та приватних осіб. Консультації, підготовка документів, представництво інтересів.",
        icon: <FileText className="h-8 w-8 text-primary" />,
        services: [
          "Юридичні консультації",
          "Підготовка та аналіз договорів",
          "Корпоративне право",
          "Трудове право",
          "Представництво в судах"
        ]
      },
      {
        title: "Бухгалтерські послуги",
        description: "Повний спектр бухгалтерських послуг для компаній та приватних підприємців. Ведення бухгалтерії, податкова звітність, оптимізація оподаткування.",
        icon: <Briefcase className="h-8 w-8 text-primary" />,
        services: [
          "Ведення бухгалтерського обліку",
          "Підготовка та подання податкової звітності",
          "Розрахунок заробітної плати",
          "Оптимізація оподаткування",
          "Підготовка річних звітів"
        ]
      },
      {
        title: "Легалізація перебування",
        description: "Допомога в оформленні документів для легального перебування іноземців у Польщі. Отримання дозволів на проживання, роботу, навчання.",
        icon: <Users className="h-8 w-8 text-primary" />,
        services: [
          "Отримання карти перебування",
          "Дозволи на роботу",
          "Реєстрація PESEL",
          "Віза для членів сім'ї",
          "Консультації з питань легалізації"
        ]
      }
    ],
    ru: [
      {
        title: "Регистрация компаний",
        description: "Полное сопровождение процесса регистрации компаний различных форм собственности. Подготовка всех необходимых документов, представительство в государственных органах.",
        icon: <Building className="h-8 w-8 text-primary" />,
        services: [
          "Регистрация ООО (Sp. z o.o.)",
          "Регистрация индивидуального предпринимателя (JDG)",
          "Регистрация товариществ",
          "Подготовка учредительных документов",
          "Представительство в KRS"
        ]
      },
      {
        title: "Юридические услуги",
        description: "Комплексное юридическое обслуживание бизнеса и частных лиц. Консультации, подготовка документов, представительство интересов.",
        icon: <FileText className="h-8 w-8 text-primary" />,
        services: [
          "Юридические консультации",
          "Подготовка и анализ договоров",
          "Корпоративное право",
          "Трудовое право",
          "Представительство в судах"
        ]
      },
      {
        title: "Бухгалтерские услуги",
        description: "Полный спектр бухгалтерских услуг для компаний и частных предпринимателей. Ведение бухгалтерии, налоговая отчетность, оптимизация налогообложения.",
        icon: <Briefcase className="h-8 w-8 text-primary" />,
        services: [
          "Ведение бухгалтерского учета",
          "Подготовка и подача налоговой отчетности",
          "Расчет заработной платы",
          "Оптимизация налогообложения",
          "Подготовка годовых отчетов"
        ]
      },
      {
        title: "Легализация пребывания",
        description: "Помощь в оформлении документов для легального пребывания иностранцев в Польше. Получение разрешений на проживание, работу, учебу.",
        icon: <Users className="h-8 w-8 text-primary" />,
        services: [
          "Получение карты пребывания",
          "Разрешения на работу",
          "Регистрация PESEL",
          "Виза для членов семьи",
          "Консультации по вопросам легализации"
        ]
      }
    ]
  };
  
  return services[language] || services.en;
};

const getPageTitles = (language: Language) => {
  const titles = {
    pl: {
      pageTitle: "Kompleksowe usługi dla biznesu i życia codziennego",
      pageDescription: "Od rejestracji spółek, przez obsługę prawną i księgowość, po legalizację pobytu i rejestrację pojazdów."
    },
    en: {
      pageTitle: "Comprehensive services for business and everyday life",
      pageDescription: "From company registration, through legal and accounting services, to residence permits and vehicle registration."
    },
    uk: {
      pageTitle: "Комплексні послуги для бізнесу та повсякденного життя",
      pageDescription: "Від реєстрації компаній, через юридичні та бухгалтерські послуги, до дозволів на проживання та реєстрації транспортних засобів."
    },
    ru: {
      pageTitle: "Комплексные услуги для бизнеса и повседневной жизни",
      pageDescription: "От регистрации компаний, через юридические и бухгалтерские услуги, до разрешений на проживание и регистрации транспортных средств."
    }
  };
  
  return titles[language] || titles.en;
};

const getFormSchema = (language: Language) => z.object({
  name: z.string().min(2, { message: getTranslation("validationNameRequired", language) }),
  email: z.string().email({ message: getTranslation("validationEmailInvalid", language) }),
  phone: z.string().optional(),
  service: z.string(),
  message: z.string().optional(),
});

type ServiceFormProps = {
  serviceName: string;
  onSuccess: () => void;
  language: Language;
};

const ServiceForm = ({ serviceName, onSuccess, language }: ServiceFormProps) => {
  const formSchema = getFormSchema(language);
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      service: serviceName,
      message: "",
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    console.log(data);
    toast.success(getTranslation("notificationSuccess", language));
    onSuccess();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getTranslation("formName", language)}</FormLabel>
              <FormControl>
                <Input placeholder={getTranslation("formPlaceholderName", language)} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getTranslation("formEmail", language)}</FormLabel>
              <FormControl>
                <Input type="email" placeholder={getTranslation("formPlaceholderEmail", language)} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getTranslation("formPhone", language)}</FormLabel>
              <FormControl>
                <Input placeholder={getTranslation("formPlaceholderPhone", language)} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="service"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getTranslation("formService", language)}</FormLabel>
              <FormControl>
                <Input {...field} disabled />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="message"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{getTranslation("formMessage", language)}</FormLabel>
              <FormControl>
                <Textarea placeholder={getTranslation("formPlaceholderMessage", language)} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full">{getTranslation("formSubmitInquiry", language)}</Button>
      </form>
    </Form>
  );
};

export default function ComplexServicesPage() {
  const [selectedService, setSelectedService] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { language } = useLanguage();

  const handleServiceClick = (service: string) => {
    setSelectedService(service);
    setIsDialogOpen(true);
  };

  const handleFormSuccess = () => {
    setIsDialogOpen(false);
  };

  const complexServices = getComplexServices(language);
  const pageTitles = getPageTitles(language);

  return (
    <div className="min-h-screen bg-background">
      {/* Page Header */}
      <div className="relative py-12 md:py-16">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://storage.googleapis.com/fenado-ai-farm-public/generated/b2322cf5-bd9e-4ee2-b9e0-776ed6e82a95.webp')",
          }}
        >
          {/* Overlay to make text readable */}
          <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/90 to-background/80"></div>
        </div>
        <div className="container relative z-10">
          <BackButton className="mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            {pageTitles.pageTitle}
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl">
            {pageTitles.pageDescription}
          </p>
        </div>
      </div>
      
      {/* Services Content */}
      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {complexServices.map((service, index) => (
            <Card key={index} className="group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border-t-3 border-t-primary">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  {service.icon}
                  <CardTitle>{service.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">{service.description}</CardDescription>
                <ul className="space-y-2 text-sm">
                  {service.services.map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter>
                <Button 
                  variant="outline" 
                  className="w-full border-primary text-primary hover:bg-primary/10 hover:text-primary"
                  onClick={() => handleServiceClick(service.title)}
                >
                  {getTranslation("servicesAskForDetails", language)} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
        
        {/* CTA Section */}
        <div className="mt-16 py-10 px-6 bg-muted/30 rounded-xl text-center">
          <h3 className="text-2xl font-semibold mb-4">{getTranslation("servicesNotFound", language)}</h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            {getTranslation("servicesFlexibleOffer", language)}
          </p>
          <Button asChild>
            <Link to="/kontakt">{getTranslation("servicesContactUs", language)}</Link>
          </Button>
        </div>
      </div>
      
      {/* Service Inquiry Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{getTranslation("servicesInquiryTitle", language)}</DialogTitle>
          </DialogHeader>
          <ServiceForm 
            serviceName={selectedService} 
            onSuccess={handleFormSuccess}
            language={language}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}